/**
 * One list for the day, built from three sources that share almost nothing.
 *
 * Pure: no DOM, no fetch, no clock. Everything it needs arrives as an
 * argument, so every rule below is testable at the boundary that matters.
 *
 * ## The two joins, and why only these two
 *
 * The three sources are strangers. A block knows a local sqlite `workItemId`.
 * A Canvas assignment knows Canvas's own numeric `id`. A calendar event knows
 * a Google event id. None of them can be matched by looking at them; they can
 * only be matched by something that already knew both, and there are exactly
 * two such things:
 *
 * - `block.gcalEventId` -- Alter created that calendar event for that session
 *   and stored the id, so this is a fact rather than a guess.
 * - `block.canvasAssignmentId` -- the `work_item` row came from that Canvas
 *   assignment, and `dayplan.py` now publishes the link.
 *
 * Titles are deliberately not a join. They were never going to match ("Lab
 * report 3" vs "Lab Report 3 (PHYS 201)"), and the failure is asymmetric: a
 * missed match shows one thing twice, which is untidy, while a false match
 * hides a real commitment off the day, which is the kind of thing you find
 * out about afterwards.
 *
 * ## Units
 *
 * The three sources disagree about time itself. Blocks publish epoch
 * SECONDS, `shapeEvent` hands back a `Date`, and Canvas sends an ISO string.
 * Sorting those together untouched puts every block in 1970 -- sorted, no
 * error, entirely wrong. Everything is normalised to epoch milliseconds on
 * the way in, and `at` is always that.
 *
 * ## Keys
 *
 * A done-flag has to name something, and `workItemId` cannot be that name:
 * an assignment with no block has no `workItemId` the browser could ever
 * know, and those are exactly the rows worth ticking. So each row carries a
 * `key`, prefixed by which id space it came from -- `w118` and `c118` are
 * different pieces of work, and unprefixed they would tick each other.
 */

/** Today's rows, ordered by when they happen. */
export function mergeToday({ blocks, assignments, events, doneIds } = {}) {
  const plan = Array.isArray(blocks) ? blocks : [];
  const canvas = Array.isArray(assignments) ? assignments : [];
  const calendar = Array.isArray(events) ? events : [];
  const done = doneIds instanceof Set ? doneIds : new Set(doneIds ?? []);

  // Both joins are id sets built once. `null` is not an id: a block with no
  // gcal event and an event with no id must not match through a shared
  // absence, which a naive `includes(undefined)` would let happen.
  const scheduledEvents = new Set(
    plan.map((b) => b.gcalEventId).filter((id) => id !== null && id !== undefined),
  );
  const scheduledCanvas = new Set(
    plan.map((b) => b.canvasAssignmentId)
      .filter((id) => id !== null && id !== undefined)
      .map(String),
  );

  /* Which of those Canvas already has an answer for.
   *
   * Built from the Canvas list rather than from the block, because the block
   * comes off the desktop's plan and the desktop does not publish whether the
   * assignment behind it was handed in through Canvas -- only `itemDone`,
   * which is a different claim with a different consequence.
   *
   * Allen, 2026-09-04: "you are still able to unselect assignments in alter
   * web that have already been marked as done/turned in/graded". The Lambda
   * flag was the first half of that (see `_handed_in` there, same date); this
   * is the second. Once Alter schedules an assignment its Canvas row is
   * suppressed below and what is left on the day is the block, which knew
   * nothing about the submission -- so it was ticked by the reconcile and
   * then untickable in silence, because the question is keyed on this flag.
   */
  const submittedCanvas = new Set(
    canvas.filter((a) => a.submitted && a.id !== null && a.id !== undefined)
      .map((a) => String(a.id)),
  );

  const rows = [];

  for (const b of plan) {
    const at = seconds(b.startAt);
    if (at === null) continue;
    rows.push(withDone({
      kind: 'block',
      source: 'plan',
      id: b.sessionId ?? null,
      sessionId: b.sessionId ?? null,
      workItemId: b.workItemId ?? null,
      canvasAssignmentId: b.canvasAssignmentId ?? null,
      // Whether CANVAS says the assignment behind this block is handed in --
      // not whether it is done. Deliberately not also setting `done` here:
      // ticking a block is a write (`recordCanvasSubmissions` in screens.js)
      // and that write is what `canvasOverrides` suppresses once somebody has
      // said "I know, and it is still not done". A block that ticked itself
      // here would come back ticked on the next render anyway, which is the
      // override not working.
      submittedOnCanvas: b.canvasAssignmentId !== null && b.canvasAssignmentId !== undefined
        && submittedCanvas.has(String(b.canvasAssignmentId)),
      title: b.title ?? '',
      courseCode: b.courseCode ?? '',
      plannedMinutes: b.plannedMinutes ?? null,
      // How long this block has actually been worked, as the desktop counts
      // it. Published by `alter/dayplan.py`; drawn here so the phone can
      // answer "how far in am I" without opening the laptop.
      workedMinutes: b.workedMinutes ?? 0,
      // Whether the ASSIGNMENT behind this block is finished, as the desktop
      // knows it. A row here is built field by field rather than spread, so
      // anything not named is silently dropped -- see `withDone`, which is
      // where this is read.
      itemDone: b.itemDone === true,
      // Why this block is not at the hour it was planned for, as the desktop
      // recorded it. Named explicitly, like everything else here: a row is
      // built field by field, so anything left out is dropped in silence --
      // which is how a tag ships that draws nothing and fails no test.
      //
      // The reason is carried through as it arrives rather than checked
      // against a list. The desktop owns what the reasons are, and a screen
      // that dropped an unfamiliar one would go quiet the next time one is
      // added, which is the failure hardest to notice.
      moves: Number(b.moves) || 0,
      movedReason: typeof b.movedReason === 'string' ? b.movedReason : '',
      allDay: false,
      at,
    }, done));
  }

  for (const a of canvas) {
    // Shown only when nothing in the plan already speaks for it.
    if (a.id !== null && a.id !== undefined && scheduledCanvas.has(String(a.id))) continue;
    const at = isoTime(a.due);
    if (at === null) continue;
    const row = withDone({
      kind: 'assignment',
      source: 'canvas',
      id: a.id ?? null,
      sessionId: null,
      // The browser has no way to know this. Only the desktop holds the
      // mapping, and it only publishes it for work it actually scheduled.
      workItemId: null,
      canvasAssignmentId: a.id === null || a.id === undefined ? null : String(a.id),
      title: a.title ?? '',
      courseCode: '',
      plannedMinutes: null,
      workedMinutes: 0,
      url: a.url ?? '',
      allDay: false,
      at,
    }, done);
    // Canvas is the authority on its own submissions, so a submitted
    // assignment is done on first paint rather than after a round trip -- and
    // it records that Canvas is the reason, because that is what makes the
    // tick something this app must not quietly take back.
    if (a.submitted) {
      row.done = true;
      row.submittedOnCanvas = true;
    }
    rows.push(row);
  }

  for (const e of calendar) {
    if (e.id !== null && e.id !== undefined && scheduledEvents.has(e.id)) continue;
    const at = eventTime(e);
    if (at === null) continue;
    rows.push(withDone({
      kind: 'event',
      source: 'calendar',
      id: e.id ?? null,
      sessionId: null,
      workItemId: null,
      canvasAssignmentId: null,
      title: e.title ?? '',
      courseCode: e.calendarName ?? '',
      plannedMinutes: null,
      workedMinutes: 0,
      location: e.location ?? '',
      allDay: Boolean(e.allDay),
      at,
    }, done));
  }

  rows.sort((x, y) => x.at - y.at);
  return rows;
}

/**
 * What a done-flag for this row is called.
 *
 * Prefixed by id space on purpose: work item 118 and Canvas assignment 118
 * are different pieces of work, and an unprefixed key would let ticking one
 * tick the other -- silently, and only for the pairs that happen to collide.
 */
export function itemKey(row) {
  if (!row) return null;
  if (row.kind === 'block') {
    return row.workItemId === null || row.workItemId === undefined
      ? null
      : `w${row.workItemId}`;
  }
  if (row.kind === 'assignment') {
    return row.id === null || row.id === undefined ? null : `c${row.id}`;
  }
  return row.id ?? null;
}

/**
 * Every key this row could have been ticked under.
 *
 * A Canvas assignment ticked while unscheduled is flagged `c90210`. If Alter
 * then schedules it, the same work arrives as a block whose key is `w118`,
 * and checking only the block's own key would quietly untick it. Both are
 * checked, so the tick survives being scheduled.
 */
function keysFor(row) {
  const keys = [];
  const own = itemKey(row);
  if (own !== null) keys.push(own);
  if (row.kind === 'block' && row.canvasAssignmentId) keys.push(`c${row.canvasAssignmentId}`);
  return keys;
}

function withDone(row, done) {
  const key = itemKey(row);
  /* Two sources, and the flags stay the authority for anything ticked here.
   *
   * `itemDone` is the desktop saying the assignment behind this block is
   * finished. Allen asked for that on 2026-08-18 -- ending a timer "should
   * strike it off on both the todo list and the alter website calendar view"
   * -- and the todo list was the only half that worked.
   *
   * Deliberately not by having the desktop write a done-flag.
   * `grantDesktopAccess` in the CDK says why in so many words: "It may read
   * the done-flags and may not set one. A tick is something a person did on
   * a phone; Alter's job is to notice it, not to assert it." So the desktop
   * publishes what it knows with the block and this draws it.
   *
   * `=== true` rather than truthiness: every plan file already in S3 was
   * written before this key existed, and `undefined` there has to read as
   * not-done rather than as anything at all. */
  const finished = row.itemDone === true;
  /* `submittedOnCanvas` defaults false here and is set by the assignment
   * branch alone. A row that is done and a row that is done *because Canvas
   * has the submission* are indistinguishable once `done` is true, and the
   * difference is what decides whether unticking it is allowed to be silent:
   * nothing in this system can write to Canvas, so unticking a submitted
   * assignment cannot un-submit it. It would simply read as outstanding work
   * that is already handed in, until the next reconcile ticked it again. */
  return {
    ...row,
    key,
    done: finished || keysFor(row).some((k) => done.has(k)),
    submittedOnCanvas: row.submittedOnCanvas === true,
  };
}

/** Epoch seconds to epoch milliseconds, or null if it is not a time. */
function seconds(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value * 1000;
}

function isoTime(value) {
  if (typeof value !== 'string') return null;
  const at = Date.parse(value);
  return Number.isNaN(at) ? null : at;
}

/**
 * When an event sits in the day.
 *
 * `shapeEvent` gives an all-day event `start: null` and only a `startDay`, so
 * reading `.start.getTime()` throws on precisely the events people put their
 * whole day into. Local midnight puts them at the top of the list, which is
 * also where they belong.
 */
function eventTime(e) {
  if (e.start instanceof Date && !Number.isNaN(e.start.getTime())) return e.start.getTime();
  if (typeof e.startDay === 'string') {
    const parts = e.startDay.split('-').map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      // Built from parts, not `Date.parse`, which reads a bare `YYYY-MM-DD`
      // as UTC and lands the previous evening for anyone west of Greenwich.
      return new Date(parts[0], parts[1] - 1, parts[2]).getTime();
    }
  }
  return null;
}
